/**
 * Marketing i18n fragment — intgOdoo (integrations/odoo).
 *
 * Source of truth for every claim on this page: content/docs/{en,nl}/admin/
 * issue-trackers.md § "Odoo Helpdesk" and the shipped code under
 * src/server/issue-trackers/odoo* + src/server/integrations/odoo/. Only shipped
 * behaviour is described: ticket create/link through the tracker abstraction,
 * stage read-back, the three opt-in outbound toggles (progress notes, status
 * push on resolve, customer reply on resolve — all off by default), the
 * approval-gated customer reply posted as a customer-visible chatter message,
 * the inbound webhook that is a trigger rather than a source of truth, and
 * settled tickets as retrieval evidence. Deliberately NOT claimed: Odoo tag
 * mirroring and knowledge-base harvesting (neither exists for Odoo).
 */

export const intgOdooEn = {
  meta: {
    title: "Odoo Helpdesk integration — ticket sync, approved replies and support evidence",
    description:
      "Create and link Odoo Helpdesk tickets, read the ticket stage back instead of guessing it, post internal progress notes, and send a customer reply only after a person approved it. Every outbound path is off by default, health is tracked per capability, and settled tickets become evidence for the next answer.",
  },
  hero: {
    eyebrow: "Odoo Helpdesk integration",
    title: "Tickets linked to Odoo,",
    highlight: "replies held for approval",
    subtitle:
      "FixControl creates and links tickets in Odoo Helpdesk and reads their stage back, so the helpdesk stays the system of record for the conversation with a customer of your organization. A reply to that customer is drafted by AI and held until someone on your team approves it — only then is it posted as a customer-visible chatter message.",
    primaryCta: "Book a demo",
    secondaryCta: "See approved replies",
    bullet1: "Replies held for approval",
    bullet2: "Outbound paths off by default",
    bullet3: "Per-capability health",
  },
  what: {
    eyebrow: "What it does",
    title: "What the Odoo Helpdesk connection covers",
  },
  features: {
    createLink: {
      title: "Ticket create and link",
      body: "An approved FixControl issue becomes an Odoo ticket: title as the ticket name, body as the description, priority mapped onto Odoo’s own 0–3 scale, and the default helpdesk team when one is configured. The issue key travels in the description; the issue keeps the ticket id and a deep link.",
    },
    stageReadBack: {
      title: "Stage read back, never guessed",
      body: "Odoo stages are free-form per helpdesk team, so FixControl reads the ticket’s stage back from Odoo instead of assuming a name. The stage a resolve should move a ticket to is one you name yourself; with none configured, nothing is pushed.",
    },
    approvedReplies: {
      title: "Approved customer replies",
      body: "Resolving an issue can queue a reply draft in the review queue. A person approves it, and only then is it posted as a customer-visible chatter message — which Odoo mails to the ticket’s followers. Reject or escalate posts nothing.",
    },
    internalNotes: {
      title: "Internal progress notes",
      body: "Engineering progress — a patch ready, a pull request opened — can be posted as internal chatter notes on the linked ticket. For agents and engineers only, never in the customer-facing thread.",
    },
    supportContext: {
      title: "Settled tickets as evidence",
      body: "Triage pulls a scoped set of related settled tickets, each with why it matched and a trust score, to weigh rather than to trust blindly. Only customer-visible messages authored by the team can count as a resolution; internal notes are excluded, fail-closed.",
    },
    inboundWebhook: {
      title: "Inbound webhook",
      body: "An Odoo automation rule can notify FixControl that a ticket changed. The notification is a trigger, not the truth: FixControl reads the authoritative stage back over the API before it acts on it.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Which Odoo paths are working, shown per path",
    description:
      "Creating a ticket, posting a customer reply, writing an internal note and pushing a stage are separate write paths with separate requirements, and each records its own health. A working ticket create can never disguise a broken reply path behind one connected badge.",
    readTickets: {
      name: "Read tickets, stages & chatter",
      body: "Reads tickets, their stage and their chatter within the linked project, to build support context and answer questions.",
    },
    createLink: {
      name: "Ticket create & link",
      body: "Creates and links tickets under the configured API key. A helpdesk team bound to a project routes incoming tickets deterministically — FixControl never guesses a project.",
    },
    publicReply: {
      name: "Customer-visible reply",
      body: "Off by default and switched on per organization; even then the draft is held until a person approves it. Without that recorded approval nothing is posted.",
    },
    internalNote: {
      name: "Internal progress note",
      body: "Off by default. Where enabled, progress is posted as internal chatter for agents and engineers — one-way, and never as a customer-visible message.",
    },
    stagePush: {
      name: "Stage push on resolve",
      body: "Off by default and only towards the stage you name. Because Odoo stages are free-form per team, the target is an explicit choice; a ticket support already settled is left alone.",
    },
    tagsAndKb: {
      name: "Odoo tags & knowledge-base harvest",
      body: "Not part of the connection. Odoo tags are records that must already exist, so FixControl labels do not become Odoo tags; harvesting a knowledge base is a Freshdesk capability, not an Odoo one.",
    },
  },
  governed: {
    eyebrow: "When something changes in Odoo",
    title: "A notification is never the source of truth",
    noteTitle: "Read back first, act second",
    noteBody:
      "An Odoo webhook payload carries little more than the ticket id, so FixControl reads the authoritative stage back over the API before doing anything with it. Only the issue’s cached tracker status is synced — never the FixControl workflow status. A settled ticket returning to an open stage notifies your managers and flags the issue as reopened; it does not re-run the work. The endpoint stays disabled until you configure a webhook secret, and every outbound Odoo write goes through the integration outbox, retrying transient failures and dead-lettering persistent ones onto the operational timeline.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Questions about the Odoo Helpdesk integration",
    q1: {
      q: "Does FixControl reply to customers in Odoo automatically?",
      a: "No. The reply path is off by default and switched on per organization, and even then a reply starts as an AI draft that is held until a person approves it. The chatter message is posted after the approval is recorded — not before.",
    },
    q2: {
      q: "What is the difference between an internal note and a customer reply?",
      a: "Both are chatter messages, but they carry a different Odoo subtype. An internal note stays with agents and engineers; a customer-visible message reaches the ticket’s followers and exists only behind the approval flow. The same distinction is applied when reading: an internal note can never count as a resolution in support evidence.",
    },
    q3: {
      q: "Does FixControl change the stage of my ticket?",
      a: "Only when you name the target stage yourself, and only if you enabled that path. Odoo stages are free-form per helpdesk team, so FixControl never guesses a stage name, and a ticket your support team already settled is left alone.",
    },
    q4: {
      q: "How do updates from Odoo reach FixControl?",
      a: "Through an automation rule on the ticket that posts to a FixControl endpoint with a shared secret; without a configured secret that endpoint is disabled. The payload is treated as a trigger and the stage is read back over the API, so a hand-authored notification cannot move a status on its own.",
    },
    q5: {
      q: "Are Odoo tags and labels mirrored?",
      a: "No. Odoo tags are records that have to exist in your database before a ticket can reference one, and FixControl does not create them on the fly — so FixControl labels stay in FixControl.",
    },
  },
  related: {
    freshdesk: {
      label: "Freshdesk",
      description: "The same governed reply flow on a Freshdesk helpdesk.",
    },
    topdesk: {
      label: "TOPdesk",
      description: "Incidents, caller-visible replies and status read-back.",
    },
    approvedReplies: {
      label: "Approved replies",
      description: "The approval flow behind every customer-facing reply.",
    },
  },
  cta: {
    title: "Watch an Odoo reply wait for approval",
    description:
      "Book a demo: an issue resolves, AI drafts the reply on the linked Odoo ticket, and the chatter message waits until someone approves it.",
    primary: "Book a demo",
    secondary: "Ask FixControl",
  },
};

export const intgOdooNl = {
  meta: {
    title: "Odoo Helpdesk-integratie — ticketkoppeling, goedgekeurde antwoorden en supportbewijs",
    description:
      "Maak en koppel Odoo Helpdesk-tickets, lees de stage terug in plaats van hem te gokken, plaats interne voortgangsnotities, en stuur een klantantwoord pas nadat iemand het heeft goedgekeurd. Elk uitgaand pad staat standaard uit, health wordt per capability bijgehouden, en afgeronde tickets worden bewijs voor het volgende antwoord.",
  },
  hero: {
    eyebrow: "Odoo Helpdesk-integratie",
    title: "Tickets gekoppeld aan Odoo,",
    highlight: "antwoorden wachten op goedkeuring",
    subtitle:
      "FixControl maakt en koppelt tickets in Odoo Helpdesk en leest hun stage terug, zodat de helpdesk het systeem van vastlegging blijft voor het gesprek met een klant van je organisatie. Een antwoord aan die klant wordt door AI opgesteld en vastgehouden tot iemand van je team het goedkeurt — pas dan verschijnt het als klantzichtbaar chatterbericht.",
    primaryCta: "Boek een demo",
    secondaryCta: "Bekijk goedgekeurde antwoorden",
    bullet1: "Antwoorden wachten op goedkeuring",
    bullet2: "Uitgaande paden standaard uit",
    bullet3: "Health per capability",
  },
  what: {
    eyebrow: "Wat het doet",
    title: "Wat de Odoo Helpdesk-koppeling dekt",
  },
  features: {
    createLink: {
      title: "Ticket aanmaken en koppelen",
      body: "Een goedgekeurd FixControl-issue wordt een Odoo-ticket: titel als ticketnaam, tekst als omschrijving, prioriteit vertaald naar de eigen 0–3-schaal van Odoo, en het standaard helpdeskteam als je dat instelt. De issue-sleutel reist mee in de omschrijving; het issue bewaart het ticket-id en een directe link.",
    },
    stageReadBack: {
      title: "Stage teruggelezen, nooit gegokt",
      body: "Odoo-stages zijn vrij in te richten per helpdeskteam, dus FixControl leest de stage van het ticket terug uit Odoo in plaats van een naam aan te nemen. De stage waar een oplossing het ticket naartoe zet, benoem je zelf; stel je er geen in, dan wordt er niets gezet.",
    },
    approvedReplies: {
      title: "Goedgekeurde klantantwoorden",
      body: "Het oplossen van een issue kan een conceptantwoord in de reviewwachtrij zetten. Iemand keurt het goed, en pas dan verschijnt het als klantzichtbaar chatterbericht — dat Odoo mailt naar de volgers van het ticket. Afwijzen of escaleren plaatst niets.",
    },
    internalNotes: {
      title: "Interne voortgangsnotities",
      body: "Voortgang uit engineering — een patch klaar, een pull request geopend — kan als interne chatternotitie op het gekoppelde ticket komen. Alleen voor agents en engineers, nooit in de klantgerichte thread.",
    },
    supportContext: {
      title: "Afgeronde tickets als bewijs",
      body: "Bij triage haalt FixControl een afgebakende set verwante afgeronde tickets op, elk met de reden van de match en een betrouwbaarheidsscore — om te wegen, niet om blind te volgen. Alleen klantzichtbare berichten van het team kunnen als oplossing tellen; interne notities vallen af, fail-closed.",
    },
    inboundWebhook: {
      title: "Inkomende webhook",
      body: "Een automatiseringsregel in Odoo kan FixControl melden dat een ticket veranderde. Die melding is een trigger, niet de waarheid: FixControl leest de gezaghebbende stage terug via de API voordat het er iets mee doet.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Welke Odoo-paden werken, per pad getoond",
    description:
      "Een ticket aanmaken, een klantantwoord plaatsen, een interne notitie schrijven en een stage zetten zijn aparte schrijfpaden met eigen vereisten, en elk pad legt zijn eigen health vast. Een werkend ticket-aanmaakpad kan zo nooit een kapot antwoordpad verbergen achter één ‘connected’-badge.",
    readTickets: {
      name: "Tickets, stages & chatter lezen",
      body: "FixControl leest tickets, hun stage en hun chatter binnen het gekoppelde project, om support-context samen te stellen en vragen te beantwoorden.",
    },
    createLink: {
      name: "Ticket aanmaken & koppelen",
      body: "Aanmaken en koppelen gebeurt onder de ingestelde API-sleutel. Een helpdeskteam dat aan een project is gekoppeld, routeert binnenkomende tickets voorspelbaar — FixControl gokt nooit een project.",
    },
    publicReply: {
      name: "Klantzichtbaar antwoord",
      body: "Standaard uit en per organisatie aan te zetten; ook dan blijft het concept vastgehouden tot iemand het goedkeurt. Zonder die vastgelegde goedkeuring wordt er niets geplaatst.",
    },
    internalNote: {
      name: "Interne voortgangsnotitie",
      body: "Standaard uit. Waar aangezet komt voortgang als interne chatter binnen voor agents en engineers — één richting, en nooit als klantzichtbaar bericht.",
    },
    stagePush: {
      name: "Stage zetten bij oplossen",
      body: "Standaard uit en alleen naar de stage die je benoemt. Omdat Odoo-stages per team vrij zijn, is het doel een expliciete keuze; een ticket dat support al had afgerond blijft ongemoeid.",
    },
    tagsAndKb: {
      name: "Odoo-tags & kennisbank-oogst",
      body: "Geen onderdeel van de koppeling. Odoo-tags zijn records die al moeten bestaan, dus FixControl-labels worden geen Odoo-tags; een kennisbank oogsten is een Freshdesk-capability, geen Odoo-capability.",
    },
  },
  governed: {
    eyebrow: "Als er iets verandert in Odoo",
    title: "Een melding is nooit de bron van waarheid",
    noteTitle: "Eerst teruglezen, dan handelen",
    noteBody:
      "Een Odoo-webhookbericht bevat weinig meer dan het ticket-id, dus FixControl leest de gezaghebbende stage terug via de API voordat het er iets mee doet. Alleen de gecachete trackerstatus van het issue wordt gesynchroniseerd — nooit de FixControl-workflowstatus. Gaat een afgerond ticket terug naar een open stage, dan worden je managers gewaarschuwd en krijgt het issue een heropend-markering; het werk wordt niet opnieuw uitgevoerd. Het endpoint blijft uit tot je een webhook-secret instelt, en elke uitgaande Odoo-schrijfactie loopt via de integratie-outbox, die tijdelijke fouten opnieuw probeert en hardnekkige naar de dead-letter op de operationele tijdlijn zet.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Vragen over de Odoo Helpdesk-integratie",
    q1: {
      q: "Antwoordt FixControl automatisch aan klanten in Odoo?",
      a: "Nee. Het antwoordpad staat standaard uit en zet je per organisatie aan, en ook dan begint een antwoord als AI-concept dat wordt vastgehouden tot iemand het goedkeurt. Het chatterbericht verschijnt nadat de goedkeuring is vastgelegd — niet ervoor.",
    },
    q2: {
      q: "Wat is het verschil tussen een interne notitie en een klantantwoord?",
      a: "Beide zijn chatterberichten, maar met een ander Odoo-subtype. Een interne notitie blijft bij agents en engineers; een klantzichtbaar bericht bereikt de volgers van het ticket en bestaat alleen achter de goedkeuringsroute. Bij lezen geldt hetzelfde onderscheid: een interne notitie kan nooit als oplossing meetellen in supportbewijs.",
    },
    q3: {
      q: "Verandert FixControl de stage van mijn ticket?",
      a: "Alleen als je de doelstage zelf benoemt, en alleen als je dat pad hebt aangezet. Odoo-stages zijn vrij per helpdeskteam, dus FixControl gokt nooit een stagenaam, en een ticket dat je supportteam al had afgerond blijft ongemoeid.",
    },
    q4: {
      q: "Hoe bereiken updates uit Odoo FixControl?",
      a: "Via een automatiseringsregel op het ticket die naar een FixControl-endpoint post met een gedeeld geheim; zonder ingesteld geheim staat dat endpoint uit. Het bericht geldt als trigger en de stage wordt via de API teruggelezen, zodat een zelfgeschreven melding op zichzelf geen status kan verzetten.",
    },
    q5: {
      q: "Worden Odoo-tags en labels gespiegeld?",
      a: "Nee. Odoo-tags zijn records die al in je database moeten bestaan voordat een ticket ernaar kan verwijzen, en FixControl maakt ze niet gaandeweg aan — dus FixControl-labels blijven in FixControl.",
    },
  },
  related: {
    freshdesk: {
      label: "Freshdesk",
      description: "Dezelfde gegoverneerde antwoordroute op een Freshdesk-helpdesk.",
    },
    topdesk: {
      label: "TOPdesk",
      description: "Incidenten, caller-zichtbare antwoorden en status teruglezen.",
    },
    approvedReplies: {
      label: "Goedgekeurde antwoorden",
      description: "De goedkeuringsroute achter elk antwoord aan een klant.",
    },
  },
  cta: {
    title: "Zie een Odoo-antwoord wachten op goedkeuring",
    description:
      "Boek een demo: een issue wordt opgelost, AI stelt het antwoord op het gekoppelde Odoo-ticket op, en het chatterbericht wacht tot iemand het goedkeurt.",
    primary: "Boek een demo",
    secondary: "Vraag het FixControl",
  },
};
